import { OfficeBackButton } from '../shared/OfficeBackButton';
import { useBaseStandardUi } from '../../lib/hooks/useBaseStandardUi';
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Users, Info, Star, Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import { useLeagueStore } from '../../lib/leagueStore';
import { 
  getFreeAgents, 
  getFreeAgentGoalies,
  getFreeAgentsStandard,
  getFreeAgentsStandardGoalie,
  getStandardPlayerSelectionStats,
  signPlayer,
  addToWatchlist,
  removeFromWatchlist,
  getWatchlistPlayerIds,
  getTeamRetentionDebt
} from '../../lib/supabase';
import { getLeagueSettings } from '../../lib/api/leagueSettings';
import { FreeAgentsTable } from './FreeAgentsTable';
import { FreeAgentsGoalieTable } from './FreeAgentsGoalieTable';
import { FreeAgent, FreeAgentGoalie, SortConfig, SortField } from './types';
import { useUserStore } from '../../lib/userStore';
import { useLanguageStore } from '../../lib/languageStore';
import { translate } from '../../lib/translations';
import { SignPlayerModal } from './SignPlayerModal';
import { PriorityListModal } from './PriorityListModal';
import { WatchlistSection } from './WatchlistSection';
import { supabase } from '../../lib/supabase';
import { usePlayerCooldown } from '../../lib/hooks/usePlayerCooldown';
import { checkPlayerCooldown, applyClientCooldown } from '../../lib/api/playerCooldown';
import { usePageTracking } from '../../lib/hooks/usePageTracking';
import { resolveErrorMessage } from '../../lib/utils/errorMessage';
import { useLeagueMode } from '../../lib/hooks/useLeagueMode';
import { EmbedBackButton } from '../shared/EmbedBackButton';
import { ModeAccessMessage } from '../shared/ModeAccessMessage';
import { sendDraftTurnEmail } from '../../lib/api/emailNotifications';
import { useEmbedStore } from '../../lib/embedStore';
import { MyTeamsModalProvider } from '../MyTeams/MyTeamsModal';
import { isTslhTenant } from '../../lib/tenantStore';
import './FreeAgentsEmbed.css';

// Arsenal embed fonts (same as MyOfficeEmbed)
const ARSENAL_FONTS_ID = 'dp-arsenal-embed-fonts';
const ARSENAL_FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=Anton&family=Open+Sans:wght@400;600;700&display=swap';

function useArsenalFonts(enabled: boolean) {
  React.useEffect(() => {
    if (!enabled) return;
    if (document.getElementById(ARSENAL_FONTS_ID)) return;
    const link = document.createElement('link');
    link.id = ARSENAL_FONTS_ID;
    link.rel = 'stylesheet';
    link.href = ARSENAL_FONTS_HREF;
    document.head.appendChild(link);
  }, [enabled]);
}

interface LeagueSettings {
  number_foward: number;
  number_def: number;
  number_goalie: number;
  number_reserve: number;
  salary_cap: boolean;
  max_salary_cap: number;
  salary_cap_reserve: boolean;
  max_salary_cap_reserve: number;
  draftMode: boolean;
  current_pick: number;
  number_scratch?: number;
  max_signing?: number;
  count_free_agent_signings?: boolean;
  pool_start_date?: string | null;
}

interface RosterCounts {
  number_of_defenders: number;
  number_of_fowards: number;
}

interface LocationState {
  fromDraft?: boolean;
  priorityMode?: boolean;
}

const NHL_TEAMS = [
  'ANA', 'UTA', 'BOS', 'BUF', 'CAR', 'CBJ', 'CGY', 'CHI', 'COL', 'DAL',
  'DET', 'EDM', 'FLA', 'LAK', 'MIN', 'MTL', 'NSH', 'NJD', 'NYI', 'NYR',
  'OTT', 'PHI', 'PIT', 'SEA', 'SJS', 'STL', 'TBL', 'TOR', 'VAN', 'VGK',
  'WSH', 'WPG',
];
const POSITION_CHECKBOXES = ['ATT', 'DEF', 'G'] as const;
type PositionGroup = (typeof POSITION_CHECKBOXES)[number];
const ITEMS_PER_PAGE = 50;

// Fonction pour obtenir l'heure actuelle au fuseau horaire de Montréal
const getMontrealTime = (): string => {
  const now = new Date();
  return now.toLocaleString('en-CA', { 
    timeZone: 'America/Toronto',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).replace(/(\d+)\/(\d+)\/(\d+), (\d+):(\d+):(\d+)/, '$3-$1-$2T$4:$5:$6');
};

type ActiveTab = 'players' | 'goalies';

export function FreeAgents() {
  // Tracker cette page
  usePageTracking('free_agent');
  const { isStandardFlexible, isBuildPhase, isLoading: isLeagueModeLoading } = useLeagueMode();
  const isBaseStandardUi = useBaseStandardUi(isStandardFlexible);
  const isEmbed = useEmbedStore((state) => state.isEmbed);
  const anchoredSignModal = isEmbed && typeof navigator !== 'undefined' && /iPhone/.test(navigator.userAgent);
  useArsenalFonts(isEmbed);

  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;
  const fromDraft = state?.fromDraft || false;
  const priorityMode = state?.priorityMode || false;
  const selectedLeague = useLeagueStore((state) => state.selectedLeague);
  const [freeAgents, setFreeAgents] = useState<FreeAgent[]>([]);
  const [freeAgentGoalies, setFreeAgentGoalies] = useState<FreeAgentGoalie[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPositions, setSelectedPositions] = useState<PositionGroup[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<string>('ALL');
  const [minSalary, setMinSalary] = useState<number>(0);
  const [maxSalary, setMaxSalary] = useState<number>(99999999);
  const [useAvailableCapForMaxSalary, setUseAvailableCapForMaxSalary] = useState(false);
  
  // État pour éviter les signatures multiples
  const [signingInProgress, setSigningInProgress] = useState<Set<number>>(new Set());
  // États pour messages inline dans les modals (ack requis)
  const [signModalMessage, setSignModalMessage] = useState<string | null>(null);
  const [signModalMessageSuccess, setSignModalMessageSuccess] = useState<boolean>(false);
  const [priorityModalMessage, setPriorityModalMessage] = useState<string | null>(null);
  const [priorityModalMessageSuccess, setPriorityModalMessageSuccess] = useState<boolean>(false);
  
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'points',
    direction: 'desc',
  });
  const [goaliesSortConfig, setGoaliesSortConfig] = useState<{
    field: keyof FreeAgentGoalie;
    direction: 'asc' | 'desc';
  }>({
    field: 'wins',
    direction: 'desc',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const currentUser = useUserStore((state) => state.user_id);
  const [activeTab, setActiveTab] = useState<ActiveTab>('players');
  const [showSignModal, setShowSignModal] = useState(false);
  const [showPriorityListModal, setShowPriorityListModal] = useState(false);
  const [showLeagueInfo, setShowLeagueInfo] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState<FreeAgent | FreeAgentGoalie | null>(null);
  const [isGoalie, setIsGoalie] = useState(false);
  const [leagueSettings, setLeagueSettings] = useState<LeagueSettings | null>(null);
  const [userSigningsUsed, setUserSigningsUsed] = useState<number | null>(null);
  const [rosterCounts, setRosterCounts] = useState<RosterCounts | null>(null);
  const [currentSalaryCap, setCurrentSalaryCap] = useState<number | null>(null);
  const [currentReserveSalaryCap, setCurrentReserveSalaryCap] = useState<number | null>(null);
  const { language } = useLanguageStore();
  const { startCooldown } = usePlayerCooldown();
  const [injuryDescriptions, setInjuryDescriptions] = useState<Array<{playerid: number, description: string}>>([]);

  // Ref + onboarding highlight
  const pageRef = React.useRef<HTMLDivElement | null>(null);
  const highlightElement = (el: HTMLElement) => {
    const originalBoxShadow = el.style.boxShadow;
    el.style.transition = 'box-shadow 0.3s, transform 0.3s';
    el.style.boxShadow = '0 0 0 6px rgba(245,166,35,0.55)';
    el.style.transform = 'translateY(-2px)';
    setTimeout(() => {
      el.style.boxShadow = originalBoxShadow;
      el.style.transform = 'translateY(0)';
    }, 2500);
  };

  useEffect(() => {
    const maybeState = (location.state as any) ?? null;
    if (maybeState && typeof maybeState.bpmOnboardingTarget === 'number') {
      const idx = maybeState.bpmOnboardingTarget as number;
      // Tour card index 3 corresponds to "Agents libres"
      if (idx === 3) {
        const target = pageRef.current ?? document.getElementById('freeagents-page');
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          highlightElement(target as HTMLElement);
        }
      }
    }
  }, [location.key]);
  
  // États pour la watchlist
  const [watchlistPlayerIds, setWatchlistPlayerIds] = useState<number[]>([]);
  const [watchlistActionInProgress, setWatchlistActionInProgress] = useState<Set<number>>(new Set());
  const [activeWatchlistTab, setActiveWatchlistTab] = useState<'watched' | 'all'>('all');

  const handleViewPlayerDetails = (player: { playerid: number }) => {
    navigate(`/players/${player.playerid}`);
  };
  
  // États pour la sélection multiple en mode priorité
  const [selectedPlayersForPriority, setSelectedPlayersForPriority] = useState<Set<number>>(new Set());
  const [bulkPrioritySettings, setBulkPrioritySettings] = useState<Map<number, boolean>>(new Map()); // true = NHL, false = Reserve
  
  // États pour la date limite des échanges
  const [tradeDeadline, setTradeDeadline] = useState<Date | null>(null);
  const [isTradeDeadlinePassed, setIsTradeDeadlinePassed] = useState(false);

  useEffect(() => {
    if (!selectedLeague) {
      navigate('/dashboard');
      return;
    }

    const leagueId = selectedLeague.id;

    async function loadSettings() {
      try {
        const { data, error } = await getLeagueSettings(leagueId);
        if (error) throw error;
        setLeagueSettings(data);
        
        // Charger la date limite des échanges
        if (data?.trade_deadline) {
          const deadline = new Date(data.trade_deadline);
          setTradeDeadline(deadline);
          
          // Vérifier si la date limite est dépassée
          const now = new Date();
          const isPassed = now > deadline;
          setIsTradeDeadlinePassed(isPassed);
        }
      } catch (error) {
        console.error('Error loading league settings:', error);
        toast.error('Failed to load league settings');
      }
    }

    loadSettings();
  }, [selectedLeague, navigate]);

  useEffect(() => {
    if (!selectedLeague || !currentUser) return;

    const leagueId = selectedLeague.id;

    async function loadSalaryCaps() {
      try {
        // Fetch main club salary cap
        const { data: mainCapData, error: mainCapError } = await supabase
          .from('get_salary_cap_per_team')
          .select('salary_cap')
          .eq('league_id', leagueId)
          .eq('user_id', currentUser)
          .eq('isnhl', true)
          .single();

        if (mainCapError && mainCapError.code !== 'PGRST116') throw mainCapError;
        setCurrentSalaryCap(mainCapData?.salary_cap || 0);

        // Fetch reserve team salary cap
        const { data: reserveCapData, error: reserveCapError } = await supabase
          .from('get_salary_cap_per_team')
          .select('salary_cap')
          .eq('league_id', leagueId)
          .eq('user_id', currentUser)
          .eq('isnhl', false)
          .single();

        if (reserveCapError && reserveCapError.code !== 'PGRST116') throw reserveCapError;
        setCurrentReserveSalaryCap(reserveCapData?.salary_cap || 0);
      } catch (error) {
        console.error('Error loading salary caps:', error);
      }
    }

    loadSalaryCaps();
  }, [selectedLeague, currentUser]);

  useEffect(() => {
    if (!selectedLeague || !currentUser) return;

    const leagueId = selectedLeague.id;

    async function loadRosterCounts() {
      try {
        const { data, error } = await supabase
          .from('get_number_type_player_per_team')
          .select('*')
          .eq('league_id', leagueId)
          .eq('user_id', currentUser)
          .single();

        if (error && error.code !== 'PGRST116') throw error;
        setRosterCounts(data || { number_of_defenders: 0, number_of_fowards: 0 });
      } catch (error) {
        console.error('Error loading roster counts:', error);
      }
    }

    // Charger le nombre de signatures déjà utilisées par l'utilisateur
    async function loadUserSigningUsage() {
      try {
        const { data, error } = await supabase
          .from('leagues_users')
          .select('number_of_sigining_used')
          .eq('league_id', selectedLeague.id)
          .eq('user_id', currentUser)
          .single();

        if (error && error.code !== 'PGRST116') throw error;
        setUserSigningsUsed(data?.number_of_sigining_used ?? 0);
      } catch (error) {
        console.error('Error loading user signing usage:', error);
        setUserSigningsUsed(0);
      }
    }

    loadRosterCounts();
    loadUserSigningUsage();
  }, [selectedLeague, currentUser]);

  useEffect(() => {
    if (!selectedLeague) {
      navigate('/dashboard');
      return;
    }

    if (isLeagueModeLoading) {
      setLoading(true);
      return;
    }

    const leagueId = selectedLeague.id;
    let cancelled = false;
  setLoading(true);

    async function loadFreeAgents() {
      try {
        const userId = currentUser ? Number(currentUser) : null;
        const [playersResponse, goaliesResponse, selectionStatsResponse] = await Promise.all([
          isStandardFlexible && userId
            ? getFreeAgentsStandard(leagueId, userId)
            : getFreeAgents(leagueId),
          isStandardFlexible && userId
            ? getFreeAgentsStandardGoalie(leagueId, userId)
            : getFreeAgentGoalies(leagueId),
          isStandardFlexible
            ? getStandardPlayerSelectionStats(leagueId)
            : Promise.resolve({ data: null, error: null })
        ]);

        if (playersResponse.error) throw playersResponse.error;
        if (goaliesResponse.error) throw goaliesResponse.error;
  if (isStandardFlexible && selectionStatsResponse.error) throw selectionStatsResponse.error;
  if (cancelled) return;

        const selectionPercentageByPlayerId = new Map(
          (selectionStatsResponse.data ?? []).map((stat) => [
            Number(stat.player_id),
            Number(stat.selection_percentage ?? 0),
          ])
        );

        if (playersResponse.data) {
          const playersData = playersResponse.data as FreeAgent[];
          const uniqueFreeAgents = playersData.reduce<FreeAgent[]>((acc, current) => {
            const isDuplicate = acc.find(
              (item) =>
                item.playerid === current.playerid &&
                item.skaterfullname === current.skaterfullname
            );
            if (!isDuplicate) {
              acc.push({
                ...current,
                ...(isStandardFlexible
                  ? { selection_percentage: selectionPercentageByPlayerId.get(Number(current.playerid)) ?? 0 }
                  : {}),
              });
            }
            return acc;
          }, []);
          setFreeAgents(uniqueFreeAgents);
        }

        // if (goaliesResponse.data) {
        //   setFreeAgentGoalies(goaliesResponse.data);
        // }
        if (goaliesResponse.data) {
            const goaliesData = goaliesResponse.data as FreeAgentGoalie[];
            const uniqueGoalies = goaliesData.reduce<FreeAgentGoalie[]>((acc, current) => {
              const isDuplicate = acc.find(
                (item) =>
                  item.playerid === current.playerid &&
                  item.goaliefullname === current.goaliefullname
              );
              if (!isDuplicate) {
                acc.push({
                  ...current,
                  ...(isStandardFlexible
                    ? { selection_percentage: selectionPercentageByPlayerId.get(Number(current.playerid)) ?? 0 }
                    : {}),
                });
              }
              return acc;
            }, []);
            setFreeAgentGoalies(uniqueGoalies);
          }

        // Fetch injury data
        const { data: injuryData, error: injuryError } = await supabase
          .from('injury_player')
          .select('playerid, description');
          
        if (injuryError) throw injuryError;
        if (cancelled) return;
        
        setInjuryDescriptions(injuryData || []);

        // Load watchlist player IDs if user is logged in
        if (currentUser) {
          const { data: watchlistIds, error: watchlistError } = await getWatchlistPlayerIds(leagueId, Number(currentUser));
          if (!cancelled && !watchlistError && watchlistIds) {
            setWatchlistPlayerIds(watchlistIds);
          }
        }
      } catch (error: any) {
        if (cancelled) return;
        console.error('Error:', error);
        toast.error('Failed to load free agents');
        setFreeAgents([]);
        setFreeAgentGoalies([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadFreeAgents();

    return () => {
      cancelled = true;
    };
  }, [selectedLeague, navigate, isStandardFlexible, isLeagueModeLoading, currentUser]);

  const handleSort = (field: SortField) => {
    setSortConfig((current) => ({
      field,
      direction:
        current.field === field && current.direction === 'desc'
          ? 'asc'
          : 'desc',
    }));
  };

  const handleGoalieSort = (field: keyof FreeAgentGoalie) => {
    setGoaliesSortConfig((current) => ({
      field,
      direction:
        current.field === field && current.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  const handleSignPlayerClick = async (player: FreeAgent | FreeAgentGoalie, isGoaliePlayer: boolean) => {
    console.log('=== DEBUG: handleSignPlayerClick called ===');
    console.log('currentUser:', currentUser);
    console.log('leagueSettings:', leagueSettings);
    console.log('player:', player);
    console.log('isGoaliePlayer:', isGoaliePlayer);

    if (!currentUser) {
      console.log('=== DEBUG: Early return - missing currentUser ===');
      return;
    }

    // Vérifier si ce joueur est déjà en cours de signature
    if (signingInProgress.has(player.playerid)) {
      console.log('=== DEBUG: Player already being signed, blocking ===');
      toast.error(language === 'fr' 
        ? 'Signature en cours pour ce joueur...'
        : 'Player signing in progress...');
      return;
    }

    // If league settings are not loaded yet, show a loading message
    if (!leagueSettings) {
      console.log('=== DEBUG: League settings not loaded yet ===');
      toast.error(language === 'fr' 
        ? 'Chargement des paramètres de la ligue en cours...'
        : 'Loading league settings...');
      return;
    }

    // Check if we're in draft mode and not accessing from draft page
    if (leagueSettings.draftMode && !fromDraft && !priorityMode) {
      toast.error(language === 'fr' 
        ? 'Les signatures d\'agents libres sont uniquement autorisées pendant le repêchage'
        : 'Free agent signings are only allowed during the draft');
      return;
    }

    if (priorityMode) {
      // In priority mode with bulk selection, toggle selection instead of opening modal
      const isSelected = selectedPlayersForPriority.has(player.playerid);
      handlePlayerSelection(player.playerid, !isSelected, isGoaliePlayer);
      return;
    }

    // Marquer le joueur comme en cours de signature
    setSigningInProgress(prev => new Set(prev.add(player.playerid)));

    try {
      // Si en mode draft, vérifier immédiatement les doublons avant d'ouvrir le modal
      if (fromDraft && selectedLeague) {
        console.log('=== DEBUG: Checking for duplicates in draft mode ===');
        
        // Vérification 1: drafted_players
        const { data: existingPick } = await supabase
          .from('drafted_players')
          .select('dp_id')
          .eq('league_id', selectedLeague.id)
          .eq('player_id', player.playerid)
          .eq('is_past_draft', false)
          .maybeSingle();

        if (existingPick) {
          console.log('=== DEBUG: Player already drafted in drafted_players ===');
          toast.error(language === 'fr'
            ? 'Ce joueur a déjà été repêché'
            : 'This player has already been drafted');
          return;
        }

        // Vérification 2: league_players_user
        const { data: alreadyInLeague } = await supabase
          .from('league_players_user')
          .select('player_id')
          .eq('league_id', selectedLeague.id)
          .eq('player_id', player.playerid)
          .maybeSingle();

        if (alreadyInLeague) {
          console.log('=== DEBUG: Player already in league_players_user ===');
          toast.error(language === 'fr'
            ? 'Ce joueur appartient déjà à une équipe'
            : 'This player already belongs to a team');
          return;
        }
      }

      // Regular sign player flow
      // Check server-side cooldown first but don't block modal display
      let shouldApplyClientCooldown = false;
      try {
        const cooldownResult = await checkPlayerCooldown(player.playerid, Number(currentUser));
        
        if (cooldownResult.cooldown) {
          // Player is on cooldown
          console.log(`Player ${player.playerid} is on cooldown. Remaining time: ${cooldownResult.remainingTime}ms`);
          toast.error(language === 'fr' 
            ? 'Ce joueur est temporairement indisponible' 
            : 'This player is temporarily unavailable');
          return;
        }
        
        // If server validation failed but not due to cooldown, fall back to client-side
        if (!cooldownResult.success && !cooldownResult.cooldown) {
          shouldApplyClientCooldown = true;
      }
    } catch (error) {
      console.error('Error checking cooldown:', error);
      // Fall back to client-side cooldown
      shouldApplyClientCooldown = true;
    }

    // Apply client-side cooldown if needed
    if (shouldApplyClientCooldown) {
      applyClientCooldown(player.playerid);
    }

      // Start the client-side cooldown
      startCooldown(player.playerid);
      
      // Continue with sign player logic - always show modal
      console.log(player);
      setSelectedPlayer(player);
      setIsGoalie(isGoaliePlayer);
      setShowSignModal(true);

    } catch (error) {
      console.error('Error in handleSignPlayerClick:', error);
      toast.error(language === 'fr'
        ? 'Erreur lors de la vérification du joueur'
        : 'Error checking player availability');
    } finally {
      // Libérer le verrou après un délai pour éviter les clics rapides
      setTimeout(() => {
        setSigningInProgress(prev => {
          const newSet = new Set(prev);
          newSet.delete(player.playerid);
          return newSet;
        });
      }, 2000); // 2 secondes de protection
    }
  };

  const handleSignPlayer = async (destination: 'nhl' | 'reserve' | 'scratch') : Promise<{ success: boolean; message?: string }> => {
    if (!selectedPlayer || !leagueSettings || !selectedLeague) return { success: false, message: translate('failed.sign.player', language) };

    const goingNHL = destination === 'nhl' || destination === 'scratch'; // scratch players count on main salary cap
    const isScratch = destination === 'scratch';

    // Draft-mode guard: block if this player already has a draft pick recorded for this league
    if (fromDraft) {
      try {
        const { data: existingPick } = await supabase
          .from('drafted_players')
          .select('dp_id')
          .eq('league_id', selectedLeague.id)
          .eq('player_id', selectedPlayer.playerid)
          .eq('is_past_draft', false)
          .maybeSingle();

        if (existingPick) {
          // Player already drafted in this league; silently block for end-users
          if (import.meta.env.DEV) {
            console.warn('Draft pick blocked: player already exists in drafted_players for this league.');
          }
          setShowSignModal(false);
          setSelectedPlayer(null);
          return { success: false, message: language === 'fr' ? 'Ce joueur a déjà été repêché' : 'This player has already been drafted' };
        }
      } catch (e) {
        // Fail-closed? Safer to allow flow; only log in dev
        if (import.meta.env.DEV) {
          console.error('Unexpected error during draft duplicate check:', e);
        }
      }

      // Draft-mode guard: block if player already belongs to any team in this league
      try {
        const { data: alreadyInLeague } = await supabase
          .from('league_players_user')
          .select('player_id')
          .eq('league_id', selectedLeague.id)
          .eq('player_id', selectedPlayer.playerid)
          .maybeSingle();

        if (alreadyInLeague) {
          if (import.meta.env.DEV) {
            console.warn('Signing blocked: player already exists in league_players_user for this league.');
          }
          return { success: false, message: language === 'fr' ? 'Ce joueur appartient déjà à une équipe' : 'This player already belongs to a team' };
        }
      } catch (e) {
        if (import.meta.env.DEV) {
          console.error('Unexpected error during league_players_user existence check:', e);
        }
      }
    }

    // Fetch retention debt and retention received for salary cap validations
    let retentionDebt = 0;
    let totalRetentionReceived = 0;
    
    if (currentUser && selectedLeague && (leagueSettings.salary_cap || leagueSettings.salary_cap_reserve)) {
      try {
        // Get debt (what we pay for other teams)
        const debtInfo = await getTeamRetentionDebt(selectedLeague.id, parseInt(currentUser));
        retentionDebt = debtInfo.totalRetainedSalary || 0;
        console.log('=== Retention debt (we pay):', formatCurrency(retentionDebt));
        
        // Get retention received (what other teams pay for us) - gives us MORE cap space
        const { data: playersData } = await supabase
          .from('getmyteamplayer')
          .select('salary, salary_retention_received_amount, isnhl')
          .eq('league_id', selectedLeague.id)
          .eq('user_id', parseInt(currentUser));

        const { data: goaliesData } = await supabase
          .from('getmyteamgoalie')
          .select('salary, salary_retention_received_amount, isnhl')
          .eq('league_id', selectedLeague.id)
          .eq('user_id', parseInt(currentUser));

        // Calculate total retention received for main club (salaries we DON'T pay)
        playersData?.forEach(p => {
          if (p.isnhl && p.salary_retention_received_amount) {
            totalRetentionReceived += p.salary_retention_received_amount;
          }
        });
        goaliesData?.forEach(g => {
          if (g.isnhl && g.salary_retention_received_amount) {
            totalRetentionReceived += g.salary_retention_received_amount;
          }
        });
        
        console.log('=== Retention received (others pay for us):', formatCurrency(totalRetentionReceived));
      } catch (error) {
        console.error('Error fetching retention info for validation:', error);
        // Continue without retention adjustments (fail-safe)
      }
    }

    // Check salary cap for NHL roster (adjusted for retention)
    if (goingNHL && leagueSettings.salary_cap) {
      const baseCapUsed = currentSalaryCap || 0;
      const newPlayerSalary = selectedPlayer.salary;
      
      // Adjust cap: original - retention received (we pay less) + retention debt (we pay more)
      const adjustedCapUsed = baseCapUsed - totalRetentionReceived + retentionDebt;
      const totalAfterSigning = adjustedCapUsed + newPlayerSalary;
      
      console.log('=== NHL Salary Cap Validation ===');
      console.log('Base cap used (raw):', formatCurrency(baseCapUsed));
      console.log('Retention received (savings):', formatCurrency(totalRetentionReceived));
      console.log('Retention debt (costs):', formatCurrency(retentionDebt));
      console.log('Adjusted cap used:', formatCurrency(adjustedCapUsed));
      console.log('New player salary:', formatCurrency(newPlayerSalary));
      console.log('Total after signing:', formatCurrency(totalAfterSigning));
      console.log('Max allowed:', formatCurrency(leagueSettings.max_salary_cap));

      if (totalAfterSigning > leagueSettings.max_salary_cap) {
        const excess = totalAfterSigning - leagueSettings.max_salary_cap;
        console.log('=== BLOCKED: Salary cap exceeded by', formatCurrency(excess));
        
        // Message détaillé
        let errorMessage = translate('cannot.sign.salary.cap', language, { amount: formatCurrency(excess) });
        if (retentionDebt > 0 || totalRetentionReceived > 0) {
          errorMessage += language === 'fr'
            ? ` (Dette: ${formatCurrency(retentionDebt)}, Économies: ${formatCurrency(totalRetentionReceived)})`
            : ` (Debt: ${formatCurrency(retentionDebt)}, Savings: ${formatCurrency(totalRetentionReceived)})`;
        }
        
        return { success: false, message: errorMessage };
      }
    }

    // Check salary cap for reserve roster (no retention debt for reserves)
    if (destination === 'reserve' && leagueSettings.salary_cap_reserve) {
      const newSalaryCap = (currentReserveSalaryCap || 0) + selectedPlayer.salary;
      
      console.log('=== Reserve Salary Cap Validation ===');
      console.log('Current reserve cap:', formatCurrency(currentReserveSalaryCap || 0));
      console.log('New player salary:', formatCurrency(selectedPlayer.salary));
      console.log('Total:', formatCurrency(newSalaryCap));
      console.log('Max allowed:', formatCurrency(leagueSettings.max_salary_cap_reserve));
      
      if (newSalaryCap > leagueSettings.max_salary_cap_reserve) {
        const excess = newSalaryCap - leagueSettings.max_salary_cap_reserve;
        console.log('=== BLOCKED: Reserve cap exceeded by', formatCurrency(excess));
        return { success: false, message: translate('cannot.sign.reserve.cap', language, {
          amount: formatCurrency(excess)
        }) };
      }
    }

    // Check roster limits
    if (isScratch) {
      // For scratch players, only check scratch limit
      const currentScratchCount = await getCurrentScratchCount();
      if (currentScratchCount + 1 > (leagueSettings.number_scratch || 0)) {
        return { success: false, message: `Limite de joueurs scratch atteinte (${currentScratchCount}/${leagueSettings.number_scratch || 0})` };
      }
    } else if (destination === 'nhl') {
      if (isGoalie) {
        // Check goalie limits - adding 1 more would exceed the limit
        const currentGoalies = await getCurrentGoalieCount();
          if (currentGoalies + 1 > leagueSettings.number_goalie) {
          return { success: false, message: translate('cannot.sign.goalie', language, {
            limit: leagueSettings.number_goalie.toString()
          }) };
        }
      } else {
        // Check player position limits
        const player = selectedPlayer as FreeAgent;
        const isDefenseman = player.positioncode === 'D';
        const currentCounts = rosterCounts || { number_of_defenders: 0, number_of_fowards: 0 };
        console.log(rosterCounts);
        if (isDefenseman) {
          // Adding 1 more defender would exceed the limit
            if (currentCounts.number_of_defenders + 1 > leagueSettings.number_def) {
            return { success: false, message: translate('cannot.sign.defender', language, {
              limit: leagueSettings.number_def.toString()
            }) };
          }
        } else {
          // Forward position (C, L, R) - adding 1 more would exceed the limit
            if (currentCounts.number_of_fowards + 1 > leagueSettings.number_foward) {
            return { success: false, message: translate('cannot.sign.forward', language, {
              limit: leagueSettings.number_foward.toString()
            }) };
          }
        }
      }
    } else if (destination === 'reserve') {
      // For reserve roster, only check total reserve players limit and salary cap
      // No position-specific limits for reserves, just the total number_reserve limit
      const currentReserveGoalies = await getCurrentReserveGoalieCount();
      const currentReservePlayers = await getCurrentReservePlayerCounts();
      const totalReservePlayers = currentReserveGoalies + currentReservePlayers.forwards + currentReservePlayers.defenders;
      
      if (totalReservePlayers + 1 > leagueSettings.number_reserve) {
        return { success: false, message: translate('cannot.sign.reserve.limit', language, {
          limit: leagueSettings.number_reserve.toString()
        }) };
      }
    } else {
      // destination === 'nhl' - Check roster limits for NHL roster (non-scratch)
      if (isGoalie) {
        // Check goalie limits - adding 1 more would exceed the limit
        const currentGoalies = await getCurrentGoalieCount();
        if (currentGoalies + 1 > leagueSettings.number_goalie) {
            return { success: false, message: translate('cannot.sign.goalie', language, {
              limit: leagueSettings.number_goalie.toString()
            }) };
          }
      } else {
        // Check player position limits
        const player = selectedPlayer as FreeAgent;
        const isDefenseman = player.positioncode === 'D';
        const currentCounts = rosterCounts || { number_of_defenders: 0, number_of_fowards: 0 };
        console.log(rosterCounts);
        if (isDefenseman) {
          // Adding 1 more defender would exceed the limit
          if (currentCounts.number_of_defenders + 1 > leagueSettings.number_def) {
            return { success: false, message: translate('cannot.sign.defender', language, {
              limit: leagueSettings.number_def.toString()
            }) };
          }
        } else {
          // Forward position (C, L, R) - adding 1 more would exceed the limit
          if (currentCounts.number_of_fowards + 1 > leagueSettings.number_foward) {
            return { success: false, message: translate('cannot.sign.forward', language, {
              limit: leagueSettings.number_foward.toString()
            }) };
          }
        }
      }
    }

    try {
      // Sign the player
      await signPlayer(selectedLeague.id, selectedPlayer.playerid, parseInt(currentUser || '0'), goingNHL, isScratch, fromDraft);
      const playerName = isGoalie ? (selectedPlayer as FreeAgentGoalie).goaliefullname : (selectedPlayer as FreeAgent).skaterfullname;

      // Mettre à jour le compteur local de signatures utilisées (non draft)
      if (!fromDraft) {
        setUserSigningsUsed(prev => (prev ?? 0) + 1);
      }

      // If we're in draft mode, insert into drafted_players table (guarded against duplicates)
      if (fromDraft) {
        try {
          const { data: existingPick } = await supabase
            .from('drafted_players')
            .select('dp_id')
            .eq('league_id', selectedLeague.id)
            .eq('player_id', selectedPlayer.playerid)
            .eq('is_past_draft', false)
            .maybeSingle();

          if (!existingPick) {
            const { error: draftPickError } = await supabase
              .from('drafted_players')
              .insert({
                league_id: selectedLeague.id,
                user_id: currentUser,
                player_id: selectedPlayer.playerid,
                player_name: isGoalie ? (selectedPlayer as FreeAgentGoalie).goaliefullname : (selectedPlayer as FreeAgent).skaterfullname,
                team_abbrevs: selectedPlayer.teamabbrevs,
                position: isGoalie ? 'G' : (selectedPlayer as FreeAgent).positioncode,
                pick_number: leagueSettings.current_pick + 1,
                picked_at: new Date().toISOString()
              });
            if (draftPickError && import.meta.env.DEV) {
              console.error('Error recording draft pick:', draftPickError);
            }
          } else if (import.meta.env.DEV) {
            console.warn('Skipped drafted_players insert: duplicate detected');
          }
        } catch (e) {
          if (import.meta.env.DEV) {
            console.error('Unexpected error when inserting draft pick:', e);
          }
        }
      }
      
      // Update the lists
      if (isGoalie) {
        setFreeAgentGoalies(freeAgentGoalies.filter((p) => p.playerid !== selectedPlayer.playerid));
      } else {
        setFreeAgents(freeAgents.filter((p) => p.playerid !== selectedPlayer.playerid));
      }

      // Refresh roster counts after signing
      const { data: newCounts } = await supabase
        .from('get_number_type_player_per_team')
        .select('*')
        .eq('league_id', selectedLeague.id)
        .eq('user_id', currentUser)
        .single();

      if (newCounts) {
        setRosterCounts(newCounts);
      }

      // Refresh salary caps
      const { data: newMainCapData } = await supabase
        .from('get_salary_cap_per_team')
        .select('salary_cap')
        .eq('league_id', selectedLeague.id)
        .eq('user_id', currentUser)
        .eq('isnhl', true)
        .single();

      if (newMainCapData) {
        setCurrentSalaryCap(newMainCapData.salary_cap);
      }

      const { data: newReserveCapData } = await supabase
        .from('get_salary_cap_per_team')
        .select('salary_cap')
        .eq('league_id', selectedLeague.id)
        .eq('user_id', currentUser)
        .eq('isnhl', false)
        .single();

      if (newReserveCapData) {
        setCurrentReserveSalaryCap(newReserveCapData.salary_cap);
      }

      // If we're in draft mode, update current_pick in league_settings
      if (fromDraft) {
        try {
          // Récupérer les paramètres du draft et l'ordre actuel
          const { data: settingsData } = await supabase
            .from('league_settings')
            .select('current_pick, isLineaire, isSerpentin')
            .eq('league_id', selectedLeague.id)
            .single();

          // Récupérer tous les drafters pour calculer le nombre d'équipes
          const { data: allDrafters } = await supabase
            .from('draft_order')
            .select('user_id, draft_position')
            .eq('league_id', selectedLeague.id)
            .order('draft_position', { ascending: true });

          if (allDrafters && settingsData) {
            const numTeams = allDrafters.length;
            const currentPick = settingsData.current_pick || 0;
            const currentRound = Math.floor(currentPick / numTeams) + 1;
            const isCurrentRoundEven = currentRound % 2 === 0;
            
            // Trouver la position actuelle du drafter
            const currentDrafter = allDrafters.find(d => d.user_id === Number(currentUser));
            
            if (currentDrafter) {
              // En mode serpentin, vérifier si c'est un choix consécutif
              if (settingsData.isSerpentin) {
                const isLastPosition = currentDrafter.draft_position === numTeams;
                const isFirstPosition = currentDrafter.draft_position === 1;
                
                const hasConsecutivePick = (isCurrentRoundEven && isFirstPosition) || 
                                        (!isCurrentRoundEven && isLastPosition);

                if (hasConsecutivePick) {
                  // Si c'est un choix consécutif, garder le même joueur actif
                  await supabase
                    .from('draft_order')
                    .update({ 
                      time_of_pick: getMontrealTime(),
                      myturn: true // Garder le tour actif
                    })
                    .eq('league_id', selectedLeague.id)
                    .eq('user_id', currentUser);

                  // Mettre à jour current_pick et retourner à la page de draft
                  await supabase
                    .from('league_settings')
                    .update({ current_pick: currentPick + 1 })
                    .eq('league_id', selectedLeague.id);

                  // // Filet de sécurité: réindexer les picks et réaligner current_pick
                  // try {
                  //   await supabase.rpc('resequence_draft_picks', { p_league_id: selectedLeague.id });
                  // } catch (reseqErr) {
                  //   console.warn('resequence_draft_picks failed', reseqErr);
                  // }

                  navigate('/draft', { replace: true });
                  return { success: true };
                }
              }

              // Si ce n'est pas un choix consécutif, procéder normalement
              // Enregistrer l'heure du choix
              const currentMontrealTime = getMontrealTime();
              
              await supabase
                .from('draft_order')
                .update({ 
                  myturn: false,
                  time_of_pick: currentMontrealTime 
                })
                .eq('league_id', selectedLeague.id)
                .eq('user_id', currentUser);

              // Trouver le prochain drafter en fonction du mode de draft
              let nextDrafter;
              if (settingsData.isSerpentin && isCurrentRoundEven) {
                // En mode serpentin et tour pair, aller vers le haut
                nextDrafter = allDrafters.find(d => 
                  d.draft_position === currentDrafter.draft_position - 1
                );
              } else {
                // Mode linéaire ou tour impair en serpentin, aller vers le bas
                nextDrafter = allDrafters.find(d => 
                  d.draft_position === currentDrafter.draft_position + 1
                );
              }

              // Si pas de prochain drafter trouvé, boucler au début/fin selon le mode
              if (!nextDrafter) {
                if (settingsData.isSerpentin && isCurrentRoundEven) {
                  nextDrafter = allDrafters[allDrafters.length - 1]; // Dernier drafter
                } else {
                  nextDrafter = allDrafters[0]; // Premier drafter
                }
              }

              // Mettre à jour le prochain drafter
              await supabase
                .from('draft_order')
                .update({ myturn: true })
                .eq('league_id', selectedLeague.id)
                .eq('user_id', nextDrafter.user_id);

              // Mettre à jour current_pick
              await supabase
                .from('league_settings')
                .update({ current_pick: currentPick + 1 })
                .eq('league_id', selectedLeague.id);

              // Email non-bloquant au prochain drafter
              try {
                const { data: nextUserData } = await supabase
                  .from('users')
                  .select('email, username')
                  .eq('user_id', nextDrafter.user_id)
                  .maybeSingle();
                if (nextUserData?.email) {
                  await sendDraftTurnEmail({
                    userEmail: nextUserData.email,
                    username: nextUserData.username || '',
                    leagueName: selectedLeague.name,
                    pickNumber: currentPick + 2,
                    type: 'current',
                  });
                }
                // Look-ahead : email au picker d'après
                const { data: upcomingPickRow } = await supabase
                  .from('draft_picks')
                  .select('user_id')
                  .eq('league_id', selectedLeague.id)
                  .eq('pick_index', currentPick + 2)
                  .maybeSingle();
                if (upcomingPickRow?.user_id) {
                  const { data: upcomingUser } = await supabase
                    .from('users')
                    .select('email, username')
                    .eq('user_id', upcomingPickRow.user_id)
                    .maybeSingle();
                  if (upcomingUser?.email) {
                    await sendDraftTurnEmail({
                      userEmail: upcomingUser.email,
                      username: upcomingUser.username || '',
                      leagueName: selectedLeague.name,
                      pickNumber: currentPick + 3,
                      type: 'upcoming',
                    });
                  }
                }
              } catch (emailErr) {
                console.error('Draft turn email failed (non-blocking):', emailErr);
              }

              // Resequence désactivé côté client
            }
          }

          navigate('/draft', { replace: true });
        } catch (error) {
          console.error('Error updating draft order after pick:', error);
          navigate('/draft', { replace: true });
        }
        return { success: true };
      }

      return { success: true, message: translate('signed.success', language, { playerName }) };

    } catch (error: any) {
      if (error?.message === 'signing_limit_reached') {
        return { success: false, message: translate('max.signing.limit.reached', language) };
      } else {
        const errorMessage = resolveErrorMessage(
          error,
          language,
          translate('failed.sign.player', 'fr'),
          translate('failed.sign.player', 'en')
        );
        console.error('Error signing player:', error);
        return { success: false, message: errorMessage };
      }
    }
  };

  const handleAddToPriorityList = async (isNHL: boolean) : Promise<{ success: boolean; message?: string }> => {
    if (!selectedPlayer || !selectedLeague || !currentUser) return { success: false, message: translate('failed.sign.player', language) };
    
    try {
      // Get the next position by counting existing entries
      const { count, error: countError } = await supabase
        .from('list_prioritaire')
        .select('*', { count: 'exact' })
        .eq('league_id', selectedLeague.id)
        .eq('user_id', currentUser);
      
      if (countError) throw countError;
      
      // Set position as count + 1 (1-based, so first item will be position 1)
      const position = (count || 0) + 1;
      console.log("insert dans list prioritaire");
      const { error } = await supabase
        .from('list_prioritaire')
        .insert({
          league_id: selectedLeague.id,
          user_id: currentUser,
          player_id: selectedPlayer.playerid,
          position: position,
          salary: selectedPlayer.salary,
          player_position: isGoalie ? 'G' : (selectedPlayer as FreeAgent).positioncode,
          isnhl: isNHL
        });
      
      if (error) {
        // Check for duplicate entry
        if (error.code === '23505') { // Postgres unique violation code
          return { success: false, message: language === 'fr' ? 'Ce joueur est déjà dans votre liste prioritaire' : 'Player already in your priority list' };
        } else {
          throw error;
        }
      } else {
        const playerName = isGoalie 
          ? (selectedPlayer as FreeAgentGoalie).goaliefullname 
          : (selectedPlayer as FreeAgent).skaterfullname;
        
        const teamType = isNHL ? 'Club Principal' : 'Équipe Réserve';
        
        // If we came from draft page, navigate back there
        if (fromDraft) {
          navigate('/draft');
        }

        return { success: true, message: `${playerName} ajouté à votre liste prioritaire (${teamType})` };
      }
    } catch (error) {
      console.error('Error adding to priority list:', error);
      return { success: false, message: language === 'fr' ? 'Erreur lors de l\'ajout à la liste prioritaire' : 'Error adding to priority list' };
    }
  };

  // Scroll helper: remonter en haut pour que les toasts positionnés en haut soient visibles (embed)
  const scrollToTop = () => {
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      // noop en cas d'environnement non DOM
    }
  };

  // Wrappers centralisés pour fermer / confirmer les modals et s'assurer qu'on remonte en haut
  const handleHideSignModal = () => {
    setShowSignModal(false);
    setSelectedPlayer(null);
    if (!anchoredSignModal) scrollToTop();
  };

  const handleConfirmSignModal = async (destination: 'nhl' | 'reserve' | 'scratch') => {
    const result = await handleSignPlayer(destination);
    if (result && result.message) {
      setSignModalMessage(result.message);
      setSignModalMessageSuccess(!!result.success);
      return;
    }
    setShowSignModal(false);
    setSelectedPlayer(null);
    if (!anchoredSignModal) scrollToTop();
  };

  const handleHidePriorityModal = () => {
    setShowPriorityListModal(false);
    setSelectedPlayer(null);
    scrollToTop();
  };

  const handleConfirmPriorityModal = async (isNHL: boolean) => {
    const result = await handleAddToPriorityList(isNHL);
    if (result && result.message) {
      setPriorityModalMessage(result.message);
      setPriorityModalMessageSuccess(!!result.success);
      return;
    }
    setShowPriorityListModal(false);
    setSelectedPlayer(null);
    scrollToTop();
  };

  const handleAcknowledgeSign = () => {
    setSignModalMessage(null);
    setSignModalMessageSuccess(false);
    setShowSignModal(false);
    setSelectedPlayer(null);
    if (!anchoredSignModal) scrollToTop();
  };

  const handleAcknowledgePriority = () => {
    setPriorityModalMessage(null);
    setPriorityModalMessageSuccess(false);
    setShowPriorityListModal(false);
    setSelectedPlayer(null);
    scrollToTop();
  };

  // Nouvelles fonctions pour la sélection multiple
  const handlePlayerSelection = (playerId: number, isSelected: boolean, isGoaliePlayer: boolean = false) => {
    const newSelected = new Set(selectedPlayersForPriority);
    
    if (isSelected) {
      newSelected.add(playerId);
      // Default to NHL assignment
      setBulkPrioritySettings(prev => new Map(prev.set(playerId, true)));
      
      // Show guidance message when first player is selected
      if (newSelected.size === 1) {
        toast.success(
          language === 'fr' 
            ? '✨ Joueur ajouté à la sélection ! Consultez le panneau en haut pour gérer vos sélections et les ajouter à votre liste prioritaire.'
            : '✨ Player added to selection! Check the panel above to manage your selections and add them to your priority list.',
          { duration: 4000 }
        );
      } else {
        // Regular confirmation for additional players
        const playerName = isGoaliePlayer 
          ? freeAgentGoalies.find(g => g.playerid === playerId)?.goaliefullname
          : freeAgents.find(p => p.playerid === playerId)?.skaterfullname;
        
        toast.success(
          language === 'fr'
            ? `${playerName} ajouté à la sélection (${newSelected.size} joueurs)`
            : `${playerName} added to selection (${newSelected.size} players)`,
          { duration: 2000 }
        );
      }
    } else {
      newSelected.delete(playerId);
      setBulkPrioritySettings(prev => {
        const newMap = new Map(prev);
        newMap.delete(playerId);
        return newMap;
      });
      
      // Show removal confirmation
      const playerName = isGoaliePlayer 
        ? freeAgentGoalies.find(g => g.playerid === playerId)?.goaliefullname
        : freeAgents.find(p => p.playerid === playerId)?.skaterfullname;
      
      toast.success(
        language === 'fr'
          ? `${playerName} retiré de la sélection`
          : `${playerName} removed from selection`,
        { duration: 1500 }
      );
    }
    
    setSelectedPlayersForPriority(newSelected);
  };

  const handleBulkTeamAssignment = (playerId: number, isNHL: boolean) => {
    setBulkPrioritySettings(prev => new Map(prev.set(playerId, isNHL)));
  };

  const handleBulkAddToPriorityList = async () => {
    if (selectedPlayersForPriority.size === 0) {
      toast.error('Aucun joueur sélectionné');
      return;
    }

    if (!selectedLeague || !currentUser) return;

    try {
      // Get current priority list count
      const { count, error: countError } = await supabase
        .from('list_prioritaire')
        .select('*', { count: 'exact' })
        .eq('league_id', selectedLeague.id)
        .eq('user_id', currentUser);
      
      if (countError) throw countError;
      
      let position = (count || 0) + 1;
      const insertPromises = [];
      let successCount = 0;
      let duplicateCount = 0;

      for (const playerId of selectedPlayersForPriority) {
        // Find player data
        let playerData: FreeAgent | FreeAgentGoalie | null = null;
        let isGoaliePlayer = false;
        
        const foundPlayer = freeAgents.find(p => p.playerid === playerId);
        if (foundPlayer) {
          playerData = foundPlayer;
        } else {
          const foundGoalie = freeAgentGoalies.find(g => g.playerid === playerId);
          if (foundGoalie) {
            playerData = foundGoalie;
            isGoaliePlayer = true;
          }
        }

        if (!playerData) continue;

        const isNHL = bulkPrioritySettings.get(playerId) ?? true;

        // Capture current position before launching async insert to avoid all inserts using the same value
        const currentPosition = position;
        position++; // Increment synchronously so next loop iteration uses the next slot

        const insertPromise = supabase
          .from('list_prioritaire')
          .insert({
            league_id: selectedLeague.id,
            user_id: currentUser,
            player_id: playerId,
            position: currentPosition,
            salary: playerData.salary,
            player_position: isGoaliePlayer ? 'G' : (playerData as FreeAgent).positioncode,
            isnhl: isNHL
          })
          .then(({ error }) => {
            if (error) {
              if (error.code === '23505') { // Duplicate entry
                duplicateCount++;
              } else {
                throw error;
              }
            } else {
              successCount++;
            }
          });

        insertPromises.push(insertPromise);
      }

      await Promise.all(insertPromises);

      // Show results
      if (successCount > 0) {
        toast.success(`${successCount} joueur(s) ajouté(s) à votre liste prioritaire`);
      }
      if (duplicateCount > 0) {
        toast.error(`${duplicateCount} joueur(s) déjà dans votre liste prioritaire`);
      }

      // Clear selections
      setSelectedPlayersForPriority(new Set());
      setBulkPrioritySettings(new Map());

      // If we came from draft page and all players were added successfully
      if (fromDraft && successCount > 0 && duplicateCount === 0) {
        setTimeout(() => {
          navigate('/draft');
        }, 1500);
      }

    } catch (error) {
      console.error('Error adding players to priority list:', error);
      toast.error('Erreur lors de l\'ajout à la liste prioritaire');
    }
  };

  const getCurrentGoalieCount = async () => {
    if (!selectedLeague || !currentUser) return 0;
    try {
      // Utiliser la vue SQL qui exclut correctement les gardiens blessés
      const { data, error } = await supabase
        .from('get_number_type_goalie_per_team')
        .select('number_of_goalie')
        .eq('league_id', selectedLeague.id)
        .eq('user_id', currentUser)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data?.number_of_goalie || 0;
    } catch (error) {
      console.error('Error getting goalie count:', error);
      return 0;
    }
  };

  const getCurrentReserveGoalieCount = async () => {
    if (!selectedLeague || !currentUser) return 0;
    try {
      // Utiliser la vue SQL qui exclut correctement les gardiens blessés
      const { data, error } = await supabase
        .from('get_number_type_goalie_per_team')
        .select('number_of_goalie_reserve')
        .eq('league_id', selectedLeague.id)
        .eq('user_id', currentUser)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data?.number_of_goalie_reserve || 0;
    } catch (error) {
      console.error('Error getting reserve goalie count:', error);
      return 0;
    }
  };

  const getCurrentReservePlayerCounts = async () => {
    if (!selectedLeague || !currentUser) return { forwards: 0, defenders: 0 };
    try {
      const { data, error } = await supabase
        .from('getmyteamplayer')
        .select('positioncode')
        .eq('league_id', selectedLeague.id)
        .eq('user_id', currentUser)
        .eq('isnhl', false);

      if (error && error.code !== 'PGRST116') throw error;

      let forwards = 0;
      let defenders = 0;

      data?.forEach((player: any) => {
        if (player.positioncode === 'D') {
          defenders++;
        } else if (['C', 'L', 'R'].includes(player.positioncode)) {
          forwards++;
        }
      });

      return { forwards, defenders };
    } catch (error) {
      console.error('Error getting reserve player counts:', error);
      return { forwards: 0, defenders: 0 };
    }
  };

  const getCurrentScratchCount = async () => {
    if (!selectedLeague || !currentUser) return 0;
    try {
      // Count ALL scratch players (both regular players and goalies) from league_players_user table
      const { data: scratchData, error: scratchError } = await supabase
        .from('league_players_user')
        .select('player_id')
        .eq('league_id', selectedLeague.id)
        .eq('user_id', currentUser)
        .eq('is_scratch', true);

      if (scratchError && scratchError.code !== 'PGRST116') throw scratchError;

      const totalScratchCount = scratchData?.length || 0;
      
      console.log(`🔍 Scratch count debug: ${totalScratchCount} total scratch players/goalies`);

      return totalScratchCount;
    } catch (error) {
      console.error('Error getting scratch count:', error);
      return 0;
    }
  };

  // Fonctions de gestion de la watchlist
  const handleAddToWatchlist = async (player: FreeAgent | FreeAgentGoalie) => {
    if (!selectedLeague || !currentUser) return;
    
    setWatchlistActionInProgress(prev => new Set([...prev, player.playerid]));
    
    try {
      const isGoaliePlayer = 'goaliefullname' in player;
      const playerName = isGoaliePlayer 
        ? (player as FreeAgentGoalie).goaliefullname 
        : (player as FreeAgent).skaterfullname;
      const playerPosition = isGoaliePlayer ? 'G' : (player as FreeAgent).positioncode;
      
      const { success, error } = await addToWatchlist(
        selectedLeague.id,
        Number(currentUser),
        player.playerid,
        playerName,
        playerPosition,
        isGoaliePlayer,
        player.salary,
        player.teamabbrevs
      );
      
      if (success) {
        setWatchlistPlayerIds(prev => [...prev, player.playerid]);
        toast.success(language === 'fr' 
          ? `${playerName} ajouté à votre liste de surveillance`
          : `${playerName} added to your watchlist`);
      } else {
        throw new Error(error?.message || 'Erreur inconnue');
      }
    } catch (error: any) {
      console.error('Error adding to watchlist:', error);
      toast.error(error.message || (language === 'fr'
        ? 'Erreur lors de l\'ajout à la liste de surveillance'
        : 'Error adding to watchlist'));
    } finally {
      setWatchlistActionInProgress(prev => {
        const newSet = new Set(prev);
        newSet.delete(player.playerid);
        return newSet;
      });
    }
  };

  const handleRemoveFromWatchlist = async (player: FreeAgent | FreeAgentGoalie) => {
    if (!selectedLeague || !currentUser) return;
    
    setWatchlistActionInProgress(prev => new Set([...prev, player.playerid]));
    
    try {
      const isGoaliePlayer = 'goaliefullname' in player;
      const playerName = isGoaliePlayer 
        ? (player as FreeAgentGoalie).goaliefullname 
        : (player as FreeAgent).skaterfullname;
      
      const { success, error } = await removeFromWatchlist(
        selectedLeague.id,
        Number(currentUser),
        player.playerid
      );
      
      if (success) {
        setWatchlistPlayerIds(prev => prev.filter(id => id !== player.playerid));
        toast.success(language === 'fr' 
          ? `${playerName} retiré de votre liste de surveillance`
          : `${playerName} removed from your watchlist`);
      } else {
        throw new Error(error?.message || 'Erreur inconnue');
      }
    } catch (error: any) {
      console.error('Error removing from watchlist:', error);
      toast.error(error.message || (language === 'fr'
        ? 'Erreur lors du retrait de la liste de surveillance'
        : 'Error removing from watchlist'));
    } finally {
      setWatchlistActionInProgress(prev => {
        const newSet = new Set(prev);
        newSet.delete(player.playerid);
        return newSet;
      });
    }
  };

  const refreshWatchlistData = async () => {
    if (!selectedLeague || !currentUser) return;
    
    try {
      const { data: watchlistIds, error } = await getWatchlistPlayerIds(selectedLeague.id, Number(currentUser));
      if (!error && watchlistIds) {
        setWatchlistPlayerIds(watchlistIds);
      }
    } catch (error) {
      console.error('Error refreshing watchlist data:', error);
    }
  };

  const togglePosition = (position: PositionGroup) => {
    setSelectedPositions((previous) =>
      previous.includes(position)
        ? previous.filter((currentValue) => currentValue !== position)
        : [...previous, position]
    );
  };

  const filteredPlayers = freeAgents
    .filter((player) => {
      if (selectedPositions.length === 0) return true;
      if (selectedPositions.includes('DEF') && player.positioncode === 'D') return true;
      if (selectedPositions.includes('ATT') && ['C', 'L', 'R'].includes(player.positioncode)) return true;
      return false;
    })
    // Exclude players under salary threshold and specific hidden IDs (frontend-only)
    .filter((player) => {
      const salary = Number(player.salary ?? 0);
      const hiddenIds = new Set([
        8484166, 8482100, 8482762, 8483466,
      ]);
      if (hiddenIds.has(Number(player.playerid))) return false;
      return salary >= 800000;
    })
    .filter((player) =>
      player.skaterfullname.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter(
      (player) =>
        selectedTeam === 'ALL' ||
        player.teamabbrevs.toLowerCase() === selectedTeam.toLowerCase()
    )
    .filter((player) => player.salary >= minSalary && player.salary <= maxSalary);

  const filteredGoalies = freeAgentGoalies
    .filter(() => selectedPositions.length === 0 || selectedPositions.includes('G'))
    // Exclude goalies under salary threshold and specific hidden IDs (frontend-only)
    .filter((goalie) => {
      const salary = Number(goalie.salary ?? 0);
      const hiddenIds = new Set([
        8484166, 8482100, 8482762, 8483466,
      ]);
      if (hiddenIds.has(Number(goalie.playerid))) return false;
      return salary >= 800000;
    })
    .filter((goalie) =>
      goalie.goaliefullname.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter(
      (goalie) =>
        selectedTeam === 'ALL' ||
        goalie.teamabbrevs.toLowerCase() === selectedTeam.toLowerCase()
    )
    .filter((goalie) => goalie.salary >= minSalary && goalie.salary <= maxSalary);

  const sortedPlayers = [...filteredPlayers].sort((a, b) => {
    const field = sortConfig.field;

    const getValue = (p: typeof a): number | string => {
      if (field === 'ppg') {
        return p.gamesplayed > 0 ? Number((p.points / p.gamesplayed).toFixed(2)) : 0;
      }
      return p[field as keyof typeof p] as unknown as number | string;
    };

    const aValue = getValue(a);
    const bValue = getValue(b);

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortConfig.direction === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    const aNum = Number(aValue as number);
    const bNum = Number(bValue as number);

    return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
  });

  const sortedGoalies = [...filteredGoalies].sort((a, b) => {
    const aValue = a[goaliesSortConfig.field];
    const bValue = b[goaliesSortConfig.field];

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return goaliesSortConfig.direction === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    return goaliesSortConfig.direction === 'asc'
      ? (aValue as number) - (bValue as number)
      : (bValue as number) - (aValue as number);
  });

  const totalPages = Math.ceil(
    (activeTab === 'players' ? sortedPlayers.length : sortedGoalies.length) / ITEMS_PER_PAGE
  );

  // Reset to first page when changing filters
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedPositions, selectedTeam, searchTerm, sortConfig, goaliesSortConfig, activeTab, minSalary, maxSalary]);

  useEffect(() => {
    const nextTab: ActiveTab =
      selectedPositions.length === 1 && selectedPositions[0] === 'G' ? 'goalies' : 'players';
    setActiveTab((previousTab) => (previousTab === nextTab ? previousTab : nextTab));
  }, [selectedPositions]);
  
  const paginatedPlayers = sortedPlayers.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const paginatedGoalies = sortedGoalies.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handlePageChange = (page: number) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const availableMainSalaryCap = leagueSettings?.salary_cap
    ? Math.max(leagueSettings.max_salary_cap - (currentSalaryCap || 0), 0)
    : null;
  const availableReserveSalaryCap = leagueSettings?.salary_cap_reserve
    ? Math.max(leagueSettings.max_salary_cap_reserve - (currentReserveSalaryCap || 0), 0)
    : null;

  useEffect(() => {
    if (!useAvailableCapForMaxSalary || availableMainSalaryCap == null) return;
    if (maxSalary !== availableMainSalaryCap) {
      setMaxSalary(availableMainSalaryCap);
    }
  }, [useAvailableCapForMaxSalary, availableMainSalaryCap, maxSalary]);

  if (loading) {
    return (
      <div className={`card shadow-lg border-0${isEmbed ? ' free-agents-embed' : ''}`}>
        <div className="card-body p-5 text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{translate('loading.free.agents', language)}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <MyTeamsModalProvider enabled={anchoredSignModal}>
    <div id="freeagents-page" ref={pageRef} tabIndex={-1} className={`card shadow-lg border-0${isEmbed ? ' free-agents-embed' : ''}`}>
      <div className="card-body p-1">
        <OfficeBackButton visible={isBaseStandardUi} />
        <EmbedBackButton />
        {isEmbed && (
          <div className="fae-header">
            <div className="fae-header__eyebrow">{selectedLeague?.name || ''}</div>
            <h1 className="fae-header__title">{language === 'fr' ? 'Agents libres' : 'Free agents'}</h1>
          </div>
        )}
        {/* Standard Flexible – build phase warning */}
        {isStandardFlexible && isBuildPhase && (
          <ModeAccessMessage
            variant="build-phase"
            title="Phase de construction en cours"
            message="La phase de construction de votre équipe est en cours. Veuillez utiliser « Bâtir mon équipe » pour ajouter des joueurs gratuitement. Les agents libres réguliers seront accessibles une fois votre équipe complète."
            actionLabel="Bâtir mon équipe"
            actionPath="/build-my-team"
          />
        )}
        {/* Masquer tout le contenu en dessous si on est en phase de construction */}
        {!(isStandardFlexible && isBuildPhase) && (<>

        {/* Vérifier si la date limite est dépassée et on n'est pas en mode draft */}
        {isTradeDeadlinePassed && !fromDraft && !priorityMode && tradeDeadline && (
          <div className="alert alert-danger d-flex align-items-center mb-4" role="alert">
            <div className="me-3">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            </div>
            <div>
              <h5 className="alert-heading mb-2">
                {language === 'fr' ? 'Date limite des signatures dépassée' : 'Free Agent Signing Deadline Passed'}
              </h5>
              <p className="mb-0">
                {language === 'fr' 
                  ? `La date limite pour signer des agents libres était le ${tradeDeadline.toLocaleDateString('fr-CA')}. Il n'est plus possible de signer des agents libres pour cette saison.`
                  : `The deadline for signing free agents was ${tradeDeadline.toLocaleDateString('en-US')}. Free agent signings are no longer allowed for this season.`
                }
              </p>
            </div>
          </div>
        )}

        {/* Afficher le contenu seulement si la date limite n'est pas dépassée OU si on est en mode draft/priorité */}
        {(!isTradeDeadlinePassed || fromDraft || priorityMode) && (
        <>
        <div style={{ marginTop: '12px' }}></div>
        
        {priorityMode && (
          <div className="alert alert-info mb-3">
            <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <Star size={16} className="me-2" />
                  <span>
                    <strong>
                      {language === 'fr'
                        ? '🎯 Mode Sélection Multiple Activé'
                        : '🎯 Multiple Selection Mode Enabled'}
                    </strong>
                    <br />
                    <small>
                      {language === 'fr'
                        ? 'Cliquez sur les joueurs pour les sélectionner, puis utilisez les boutons ci-dessous pour les ajouter en une fois à votre liste prioritaire.'
                        : 'Click on players to select them, then use the buttons below to add them all at once to your priority list.'}
                    </small>
                  </span>
                </div>
                {selectedPlayersForPriority.size > 0 && (
                  <div className="d-flex align-items-center gap-3">
                    <span className="badge bg-primary fs-6">
                      {selectedPlayersForPriority.size} joueur(s) sélectionné(s)
                    </span>
                    <button
                      className="btn btn-success btn-sm"
                      onClick={handleBulkAddToPriorityList}
                    >
                      <Star size={14} className="me-1" />
                      Ajouter à la liste prioritaire
                    </button>
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => {
                        setSelectedPlayersForPriority(new Set());
                        setBulkPrioritySettings(new Map());
                      }}
                    >
                      Tout désélectionner
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

        {leagueSettings?.draftMode && !fromDraft && !priorityMode && (
          <div className="alert alert-warning mb-4">
            <div className="d-flex align-items-center">
              <Info size={20} className="me-2" />
              <span>
                {language === 'fr'
                  ? 'Le repêchage est en cours. Les signatures d\'agents libres sont uniquement autorisées pendant votre tour de repêchage.'
                  : 'Draft is in progress. Free agent signings are only allowed during your draft turn.'}
              </span>
            </div>
          </div>
        )}

        {leagueSettings && (isTslhTenant() || (!priorityMode && leagueSettings.count_free_agent_signings === false)) && (
          <div className="alert alert-info d-flex align-items-start mb-4" role="alert">
            <Info size={18} className="me-2 mt-1 flex-shrink-0" />
            <div>
              {isTslhTenant() ? (
                <p className="small mb-0">
                  {translate('tslh.signing.limit.intro', language)}{' '}
                  <strong>{translate('tslh.signing.limit.count', language)}</strong>
                  {translate('tslh.signing.limit.instructions', language)}{' '}
                  <strong>{translate('tslh.signing.limit.section', language)}</strong>.
                </p>
              ) : (
                <>
                  <strong className="d-block mb-1">
                    {(() => {
                      const rawDate = leagueSettings.pool_start_date;
                      const parsed = rawDate ? new Date(rawDate) : null;
                      if (parsed && !isNaN(parsed.getTime())) {
                        const formatted = parsed.toLocaleDateString(
                          language === 'fr' ? 'fr-CA' : 'en-CA',
                          { year: 'numeric', month: 'long', day: 'numeric' }
                        );
                        return language === 'fr'
                          ? `Signatures d'agents libres(a condition de respecter les limites d'aliignement vous pouvez par exemple libérer un joueur de votre alignement pour signer un agent libre) illimitées jusqu'au ${formatted}`
                          : `Unlimited free agent signings until ${formatted}`;
                      }
                      return translate('unlimited.fa.signings.title', language);
                    })()}
                  </strong>
                  <span className="small">
                    {translate('unlimited.fa.signings.message', language)}
                  </span>
                </>
              )}
            </div>
          </div>
        )}

        {leagueSettings && !priorityMode && (
          <div className={`card border-0 bg-light ${showLeagueInfo ? 'mb-4' : 'mb-2'}`}>
            <div className={`card-body ${showLeagueInfo ? '' : 'py-2'}`}>
              <div className={`d-flex align-items-center justify-content-between ${showLeagueInfo ? 'mb-3' : 'mb-0'}`}>
                <div className="d-flex align-items-center">
                  <Info size={20} className="text-primary me-2" />
                  <h5 className="mb-0">{translate('league.info', language)}</h5>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => setShowLeagueInfo((v) => !v)}
                >
                  {showLeagueInfo
                    ? (language === 'fr' ? 'Masquer' : 'Hide')
                    : (language === 'fr' ? 'Afficher' : 'Show')}
                </button>
              </div>
              {showLeagueInfo && (
              <div className="row g-3">
                <div className="col-md-4">
                  <div className="card h-100">
                    <div className="card-body">
                      <h6 className="card-title">{translate('roster.limits', language)}</h6>
                      <ul className="list-unstyled mb-0">
                        <li>{translate('forwards', language)}: {leagueSettings.number_foward}</li>
                        <li>{translate('defensemen', language)}: {leagueSettings.number_def}</li>
                        <li>{translate('goalies', language)}: {leagueSettings.number_goalie}</li>
                      </ul>
                    </div>
                  </div>
                </div>
                {leagueSettings.salary_cap && (
                  <div className="col-md-4">
                    <div className="card h-100">
                      <div className="card-body">
                        <h6 className="card-title">{translate('main.club.salary.cap', language)}</h6>
                        <div className="d-flex flex-column">
                          <p className="mb-2">
                            {formatCurrency(currentSalaryCap || 0)} / {formatCurrency(leagueSettings.max_salary_cap)}
                          </p>
                          <p className="text-muted mb-0">
                            {translate('available', language)}: {formatCurrency(leagueSettings.max_salary_cap - (currentSalaryCap || 0))}
                          </p>
                          <div className="progress mt-2" style={{ height: '8px' }}>
                            <div
                              className={`progress-bar ${
                                (currentSalaryCap || 0) > leagueSettings.max_salary_cap
                                  ? 'bg-danger'
                                  : 'bg-success'
                              }`}
                              role="progressbar"
                              style={{
                                width: `${Math.min(
                                  ((currentSalaryCap || 0) / leagueSettings.max_salary_cap) * 100,
                                  100
                                )}%`,
                              }}
                              aria-valuenow={currentSalaryCap || 0}
                              aria-valuemin={0}
                              aria-valuemax={leagueSettings.max_salary_cap}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {leagueSettings.salary_cap_reserve && (
                  <div className="col-md-4">
                    <div className="card h-100">
                      <div className="card-body">
                        <h6 className="card-title">{translate('reserve.team.salary.cap', language)}</h6>
                        <div className="d-flex flex-column">
                          <p className="mb-2">
                            {formatCurrency(currentReserveSalaryCap || 0)} / {formatCurrency(leagueSettings.max_salary_cap_reserve)}
                          </p>
                          <p className="text-muted mb-0">
                            {translate('available', language)}: {formatCurrency(leagueSettings.max_salary_cap_reserve - (currentReserveSalaryCap || 0))}
                          </p>
                          <div className="progress mt-2" style={{ height: '8px' }}>
                            <div
                              className={`progress-bar ${
                                (currentReserveSalaryCap || 0) > leagueSettings.max_salary_cap_reserve
                                  ? 'bg-danger'
                                  : 'bg-success'
                              }`}
                              role="progressbar"
                              style={{
                                width: `${Math.min(
                                  ((currentReserveSalaryCap || 0) / leagueSettings.max_salary_cap_reserve) * 100,
                                  100
                                )}%`,
                              }}
                              aria-valuenow={currentReserveSalaryCap || 0}
                              aria-valuemin={0}
                              aria-valuemax={leagueSettings.max_salary_cap_reserve}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div className="col-md-4">
                  <div className="card h-100">
                    <div className="card-body">
                      <h6 className="card-title">{language === 'fr' ? 'Limite de signatures' : 'Signing limit'}</h6>
                      {isTslhTenant() ? (() => {
                        const maxSignings = leagueSettings.max_signing != null ? Number(leagueSettings.max_signing) : 15;
                        const used = userSigningsUsed ?? 0;
                        const usageClass =
                          maxSignings > 0 && used >= maxSignings
                            ? 'text-danger'
                            : maxSignings > 0 && used >= maxSignings - 1
                            ? 'text-warning'
                            : 'text-success';
                        return (
                          <>
                            <div className={`fw-bold fs-4 lh-1 mb-2 ${usageClass}`}>{used} / {maxSignings}</div>
                            <p className="mb-0 small text-muted">
                              <strong>{translate('tslh.signing.limit.count', language)}</strong>
                            </p>
                          </>
                        );
                      })() : leagueSettings.count_free_agent_signings === false ? (
                        <>
                          <div className="d-flex align-items-center mb-2">
                            <span className="badge bg-success">
                              {translate('unlimited.fa.signings.card.title', language)}
                            </span>
                          </div>
                          <p className="mb-0 small text-muted">
                            {translate('unlimited.fa.signings.card.subtitle', language)}
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="mb-2">
                            {(leagueSettings as any)?.max_signing != null ? (leagueSettings as any).max_signing : 'N/A'} {language === 'fr' ? ' signatures par saison' : ' signings per season'}
                          </p>
                          <p className="text-muted mb-0">
                            {language === 'fr' ? 'Utilisées' : 'Used'}: {userSigningsUsed ?? 0}
                            {((leagueSettings as any)?.max_signing != null) && (
                              <>
                                {' — '}
                                {language === 'fr' ? 'Restantes' : 'Remaining'}: {Math.max(((leagueSettings as any).max_signing || 0) - (userSigningsUsed ?? 0), 0)}
                              </>
                            )}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              )}
            </div>
          </div>
        )}

        {priorityMode && selectedPlayersForPriority.size > 0 && (
          <div className="card mb-4 border-primary">
            <div className="card-header bg-primary text-white">
              <h6 className="mb-0">
                <Star size={16} className="me-2" />
                Joueurs sélectionnés ({selectedPlayersForPriority.size})
              </h6>
            </div>
            <div className="card-body">
              <div className="row">
                {Array.from(selectedPlayersForPriority).map(playerId => {
                  const player = freeAgents.find(p => p.playerid === playerId) || 
                               freeAgentGoalies.find(g => g.playerid === playerId);
                  const isGoalie = !freeAgents.find(p => p.playerid === playerId);
                  const isNHL = bulkPrioritySettings.get(playerId) ?? true;
                  
                  if (!player) return null;
                  
                  return (
                    <div key={playerId} className="col-md-6 mb-2">
                      <div className="d-flex align-items-center justify-content-between border rounded p-2">
                        <div className="d-flex align-items-center">
                          <span className="me-2">
                            {isGoalie ? (player as FreeAgentGoalie).goaliefullname : (player as FreeAgent).skaterfullname}
                          </span>
                          <small className="text-muted">
                            ({isGoalie ? 'G' : (player as FreeAgent).positioncode})
                          </small>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <select
                            className="form-select form-select-sm"
                            style={{ width: 'auto' }}
                            value={isNHL ? 'nhl' : 'reserve'}
                            onChange={(e) => handleBulkTeamAssignment(playerId, e.target.value === 'nhl')}
                          >
                            <option value="nhl">Club Principal</option>
                            <option value="reserve">Équipe Réserve</option>
                          </select>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handlePlayerSelection(playerId, false, isGoalie)}
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="d-flex justify-content-end gap-2 mt-3">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setSelectedPlayersForPriority(new Set());
                    setBulkPrioritySettings(new Map());
                  }}
                >
                  Tout désélectionner
                </button>
                <button
                  className="btn btn-success"
                  onClick={handleBulkAddToPriorityList}
                >
                  <Star size={16} className="me-2" />
                  Ajouter {selectedPlayersForPriority.size} joueur(s) à la liste
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Filtres */}
        <div className="card border-0 bg-light mb-4">
          <div className="card-body">
            <div className="d-flex align-items-center mb-3">
              <Info size={20} className="text-primary me-2" />
              <h5 className="mb-0">{language === 'fr' ? 'Filtres' : 'Filters'}</h5>
            </div>
            <div className="row g-2">
                <div className="col-md-4">
                  <label className="form-label small mb-1">{translate('filter.name', language)}</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder={translate('search.player', language)}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label small mb-1">{translate('filter.team', language)}</label>
                  <select
                    className="form-select form-select-sm"
                    value={selectedTeam}
                    onChange={(e) => setSelectedTeam(e.target.value)}
                  >
                    <option value="ALL">{translate('all.teams', language)}</option>
                    {NHL_TEAMS.map((team) => (
                      <option key={team} value={team}>
                        {team}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-4">
                  <label className="form-label small mb-1">{language === 'fr' ? 'Salaire ($)' : 'Salary ($)'}</label>
                  <div className="row g-1">
                    <div className="col-6">
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        placeholder={language === 'fr' ? 'Min' : 'Min'}
                        value={minSalary === 0 ? '' : minSalary}
                        min={0}
                        onChange={(e) => setMinSalary(e.target.value === '' ? 0 : Number(e.target.value))}
                      />
                    </div>
                    <div className="col-6">
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        placeholder={language === 'fr' ? 'Max' : 'Max'}
                        value={maxSalary === 99999999 ? '' : maxSalary}
                        min={0}
                        onChange={(e) => {
                          setUseAvailableCapForMaxSalary(false);
                          setMaxSalary(e.target.value === '' ? 99999999 : Number(e.target.value));
                        }}
                      />
                    </div>
                  </div>
                  {availableMainSalaryCap != null && (
                    <>
                      <div className="form-check mt-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="free-agents-use-available-cap"
                          checked={useAvailableCapForMaxSalary}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setUseAvailableCapForMaxSalary(checked);
                            if (checked) {
                              setMaxSalary(availableMainSalaryCap);
                            }
                          }}
                        />
                        <label className="form-check-label small" htmlFor="free-agents-use-available-cap">
                          {language === 'fr'
                            ? 'Max disponible (club principal)'
                            : 'Use available max (main club)'}
                        </label>
                      </div>
                      <div className="small text-muted mt-1">
                        {language === 'fr' ? 'Cap disponible (principal): ' : 'Available cap (main): '}
                        {formatCurrency(availableMainSalaryCap)}
                      </div>
                      {availableReserveSalaryCap != null && (
                        <div className="small text-muted">
                          {language === 'fr' ? 'Cap disponible (réserve): ' : 'Available cap (reserve): '}
                          {formatCurrency(availableReserveSalaryCap)}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Navigation principale: Tous / Surveillés */}
                <div className="col-12">
                  <label className="form-label">{language === 'fr' ? 'Type de liste' : 'List Type'}</label>
                  <ul className="nav nav-tabs">
                    <li className="nav-item">
                      <button
                        className={`nav-link ${activeWatchlistTab === 'all' ? 'active' : ''}`}
                        onClick={() => setActiveWatchlistTab('all')}
                      >
                        <Users size={18} className="me-2" />
                        {language === 'fr' ? 'Tous les Agents Libres' : 'All Free Agents'}
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={`nav-link ${activeWatchlistTab === 'watched' ? 'active' : ''}`}
                        onClick={() => setActiveWatchlistTab('watched')}
                      >
                        <Eye size={18} className="me-2" />
                        {language === 'fr' ? 'Joueurs Surveillés' : 'Watched Players'}
                        <span className="badge bg-secondary ms-2">{watchlistPlayerIds.length}</span>
                      </button>
                    </li>
                  </ul>
                </div>

                <div className="col-12">
                  <label className="form-label small mb-1">{translate('filter.position', language)}</label>
                  <div className="d-flex flex-wrap gap-2">
                    {POSITION_CHECKBOXES.map((position) => (
                      <div key={position} className="form-check form-check-inline me-1 mb-1">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`free-agents-pos-${position}`}
                          checked={selectedPositions.includes(position)}
                          onChange={() => togglePosition(position)}
                        />
                        <label className="form-check-label small" htmlFor={`free-agents-pos-${position}`}>
                          {position}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="col-12 d-flex flex-wrap gap-2 align-items-center">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedTeam('ALL');
                      setSelectedPositions([]);
                      setMinSalary(0);
                      setMaxSalary(99999999);
                      setUseAvailableCapForMaxSalary(false);
                    }}
                  >
                    {language === 'fr' ? 'Réinitialiser les filtres' : 'Reset filters'}
                  </button>
                  <small className="text-muted">{language === 'fr' ? '50 joueurs par page (fixe)' : '50 players per page (fixed)'}</small>
                </div>
              </div>
          </div>
        </div>

        {/* Contenu conditionnel */}
        {activeWatchlistTab === 'watched' ? (
          <WatchlistSection 
            language={language} 
            onRefresh={refreshWatchlistData} 
            isStandardFlexible={isStandardFlexible}
          />
        ) : (
          <>
        {activeTab === 'players' && (
          <>
            {paginatedPlayers.length === 0 ? (
              <div className="text-center">
                <p>{translate('no.free.agents', language)}</p>
              </div>
            ) : (
              <FreeAgentsTable
                players={paginatedPlayers}
                sortConfig={sortConfig}
                onSort={handleSort}
                onSign={(player) => handleSignPlayerClick(player, false)}
                language={language}
                priorityMode={priorityMode}
                actionLabel={priorityMode ? "Sélectionner" : translate('sign', language)}
                injuredPlayers={injuryDescriptions}
                selectedPlayers={priorityMode ? selectedPlayersForPriority : undefined}
                bulkPrioritySettings={priorityMode ? bulkPrioritySettings : undefined}
                onTeamAssignment={priorityMode ? handleBulkTeamAssignment : undefined}
                signingInProgress={signingInProgress}
                onViewDetails={handleViewPlayerDetails}
                watchlistPlayerIds={watchlistPlayerIds}
                onAddToWatchlist={handleAddToWatchlist}
                onRemoveFromWatchlist={handleRemoveFromWatchlist}
                watchlistActionInProgress={watchlistActionInProgress}
                isEmbed={isEmbed}
                nameActsAsSign={isEmbed}
                showSelectionPercentage={isStandardFlexible}
              />
            )}
          </>
        )}

        {activeTab === 'goalies' && (
          <>
            {paginatedGoalies.length === 0 ? (
              <div className="text-center">
                <p>{translate('no.free.goalies', language)}</p>
              </div>
            ) : (
              <FreeAgentsGoalieTable
                goalies={paginatedGoalies}
                sortConfig={goaliesSortConfig}
                onSort={handleGoalieSort}
                onSign={(goalie) => handleSignPlayerClick(goalie, true)}
                language={language}
                priorityMode={priorityMode}
                actionLabel={priorityMode ? "Sélectionner" : translate('sign', language)}
                injuredPlayers={injuryDescriptions}
                selectedPlayers={priorityMode ? selectedPlayersForPriority : undefined}
                bulkPrioritySettings={priorityMode ? bulkPrioritySettings : undefined}
                onTeamAssignment={priorityMode ? handleBulkTeamAssignment : undefined}
                signingInProgress={signingInProgress}
                onViewDetails={handleViewPlayerDetails}
                watchlistPlayerIds={watchlistPlayerIds}
                onAddToWatchlist={handleAddToWatchlist}
                onRemoveFromWatchlist={handleRemoveFromWatchlist}
                watchlistActionInProgress={watchlistActionInProgress}
                isEmbed={isEmbed}
                nameActsAsSign={isEmbed}
                showSelectionPercentage={isStandardFlexible}
              />
            )}
          </>
        )}

        <div className="d-flex justify-content-center mt-4">
          <button
            className="btn btn-primary mx-2"
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
          >
            {translate('previous', language)}
          </button>
          <span className="mx-2">
            {translate('page', language)} {currentPage} {translate('of', language)} {totalPages}
          </span>
          <button
            className="btn btn-primary mx-2"
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
          >
            {translate('next', language)}
          </button>
        </div>
        </>
        )
        }
        </>
        )}
        
        </>)}

        {/* Modals - toujours disponibles */}
        <SignPlayerModal
          show={showSignModal}
          onHide={handleHideSignModal}
          onConfirm={handleConfirmSignModal}
          playerName={isGoalie ? (selectedPlayer as FreeAgentGoalie)?.goaliefullname : (selectedPlayer as FreeAgent)?.skaterfullname}
          language={language}
          allowReserve={leagueSettings ? leagueSettings.number_reserve > 0 : true}
          allowScratch={leagueSettings ? (leagueSettings.number_scratch !== 0) : true}
          message={signModalMessage}
          messageSuccess={signModalMessageSuccess}
          onAcknowledge={handleAcknowledgeSign}
        />

        <PriorityListModal
          show={showPriorityListModal}
          onHide={handleHidePriorityModal}
          onConfirm={handleConfirmPriorityModal}
          playerName={isGoalie ? (selectedPlayer as FreeAgentGoalie)?.goaliefullname : (selectedPlayer as FreeAgent)?.skaterfullname}
          language={language}
          message={priorityModalMessage}
          messageSuccess={priorityModalMessageSuccess}
          onAcknowledge={handleAcknowledgePriority}
        />
      </div>
    </div>
    </MyTeamsModalProvider>
  );
}

